// "Verify it's you" — RON-5465. Prototype region: `.verify-content`.
//
// The gate in front of the pack, and the reason the emailed link is not a credential.
//
// §1: the link carries a bare guid that never expires and travels in forwarded mail. The guid says
// WHICH pack; the code proves WHO. `request-code` mails the code to the address ON THE PACK —
// never one in the request — so holding the link is not enough; you need the mailbox.
//
// TWO THINGS THIS SCREEN MUST NOT DO, both of which would leak one customer's data to another:
//
//   · say whether the guid is real. `request-code` answers identically for a live pack, an unknown
//     guid and a finished one, so there is nothing to branch on and the screen must not invent a
//     branch the API cannot satisfy.
//   · show the email, even masked. Nothing about the mailbox comes back at all — see below.

import { useState } from "react";
import { useTranslation } from "react-i18next";

const CODE_LENGTH = 6;

// §1: five code requests per hour, per contractor. The prototype counts them out loud ("request 2
// of 5") and that is worth keeping: somebody who has asked twice can see where the ceiling is
// before they hit it, and a contractor locked out for an hour cannot get onto site.
const MAX_CODE_REQUESTS = 5;

const PortalVerify = ({
  isBusy,
  error,
  codeRequested,
  codeRequestCount,
  onRequestCode,
  onVerify,
}) => {
  const { t } = useTranslation("common");
  const [code, setCode] = useState("");

  // Step A until a code has been asked for, step B after.
  //
  // CORRECTED 06-October-2026. This used to be `Boolean(maskedEmail)`, on the belief that the
  // masked address was "the evidence that the request went through". The real `request-code`
  // returns `{ sent: true }` and no address at all, so against the live API this was permanently
  // false and the code box NEVER APPEARED — the portal would have been a dead end for everyone.
  //
  // The prototype gates on a plain boolean too (`verifyState.codeSent`), so this also matches it.
  const hasRequested = codeRequested === true;

  const onCodeChange = (value) => {
    // Digits only, capped. A code pasted from an email often arrives with a stray space, and
    // stripping it is kinder than refusing it.
    setCode(value.replace(/\D/g, "").slice(0, CODE_LENGTH));
  };

  const canSubmit = code.length === CODE_LENGTH && !isBusy;

  return (
    <div className="portal-verify">
      <div className="portal-verify__card">
        <span className="portal-verify__icon" aria-hidden="true">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </span>

        <h1 className="portal-verify__title">{t("react_portal_verify_title")}</h1>

        <p className="portal-verify__sub">
          {t("react_portal_verify_sub")}
        </p>

        {!hasRequested ? (
          <>
            <div className="portal-verify__well">
              <p className="portal-verify__well-label">{t("react_portal_verify_send_to")}</p>
              {/* Nothing is shown here until the server has said what it will send to — this
                  screen never knows the address on its own. */}
              <p className="portal-verify__email">{t("react_portal_verify_your_email")}</p>
            </div>

            <button
              type="button"
              className="portal-verify__btn"
              disabled={isBusy}
              onClick={onRequestCode}
            >
              {t(isBusy ? "react_loading" : "react_portal_send_code")}
            </button>

            {error && <p className="portal-verify__error">{error}</p>}

            {/* The way out for somebody whose email on file is wrong. They cannot fix it here,
                and we cannot name who to ask: the host's name rides on the pack, and the pack
                needs a session. So this points at the invitation they already have. */}
            <p className="portal-verify__help">{t("react_portal_verify_not_your_email")}</p>
          </>
        ) : (
          <>
            <p className="portal-verify__sent">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {/* The prototype names a masked address here. We cannot: the server sends none, and
                  inventing one would confirm the guid is real to anyone holding the link. This
                  says the same useful thing — go and look in your email — and nothing else. On a
                  repeat request the prototype's "request N of 5" takes over, which we can match
                  because the count is ours. */}
              {codeRequestCount > 1
                ? t("react_portal_code_resent")
                    .replace("%n", String(codeRequestCount))
                    .replace("%m", String(MAX_CODE_REQUESTS))
                : t("react_portal_code_sent")}
            </p>

            <p className="portal-verify__prompt">{t("react_portal_enter_code")}</p>

            <input
              type="text"
              className="portal-verify__code"
              value={code}
              onChange={(event) => onCodeChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && canSubmit) onVerify(code);
              }}
              // `inputMode` brings up the number pad on a phone without rejecting a paste, which
              // `type="number"` would also do while adding spinners nobody wants.
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={CODE_LENGTH}
              placeholder="000000"
              // The browser and the OS can offer the code straight from the message.
              autoComplete="one-time-code"
              aria-label={t("react_portal_enter_code")}
              autoFocus
            />

            {error && (
              <p className="portal-verify__error" role="alert">
                {error}
              </p>
            )}

            <button
              type="button"
              className="portal-verify__btn"
              disabled={!canSubmit}
              onClick={() => onVerify(code)}
            >
              {t(isBusy ? "react_loading" : "react_portal_verify_continue")}
            </button>

            <p className="portal-verify__help">
              {t("react_portal_no_code")}{" "}
              <button type="button" className="portal-verify__link" onClick={onRequestCode}>
                {t("react_portal_resend")}
              </button>
              <br />
              {t("react_portal_code_expiry")}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PortalVerify;
